// src/pages/dashboard/components/TopSellingProducts.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Award, Package } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { formatCurrency } from '../../../utils/currencyUtil';
import { getTopProducts } from '../../../services/api/salesReport';
import DateRangePresetSelect, { getDateRangeByPreset } from './DateRangePresetSelect';
import selectStyles from './DateRangePresetSelect.module.css';
import styles from './TopSellingProducts.module.css';

const TopSellingProducts = ({ shopId, currency }) => {
  const { t } = useLocalization();
  const [datePreset, setDatePreset] = useState('thisMonth');
  const dateRange = useMemo(() => getDateRangeByPreset(datePreset), [datePreset]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(10);

  useEffect(() => {
    fetchTopProducts();
  }, [shopId, dateRange, limit]);

  const fetchTopProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        limit
      };
      if (shopId && shopId !== 'all') {
        params.shopId = shopId;
      }
      if (dateRange?.from) {
        params.dateFrom = dateRange.from;
      }
      if (dateRange?.to) {
        params.dateTo = dateRange.to;
      }
      const response = await getTopProducts(params);
      setData(response);
    } catch (err) {
      console.error('Error fetching top products:', err);
      setError(err.message || t('dashboard.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value || 0);
  };

  const getMaxQuantity = () => {
    if (!data || !data.products || data.products.length === 0) return 0;
    return Math.max(...data.products.map(p => p.quantitySold));
  };

  const getRankBadgeColor = (rank) => {
    switch(rank) {
      case 1: return '#fbbf24'; // Gold
      case 2: return '#94a3b8'; // Silver
      case 3: return '#cd7f32'; // Bronze
      default: return '#e5e7eb'; // Gray
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.topProducts')}</h3>
          <div className={selectStyles.headerActions}>
            <DateRangePresetSelect value={datePreset} onChange={setDatePreset} />
            <Award size={20} />
          </div>
        </div>
        <div className={styles.skeleton}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className={styles.skeletonItem}>
              <div className={styles.skeletonBar}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.topProducts')}</h3>
          <div className={selectStyles.headerActions}>
            <DateRangePresetSelect value={datePreset} onChange={setDatePreset} />
            <Award size={20} />
          </div>
        </div>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={fetchTopProducts} className={styles.retryButton}>
            {t('dashboard.tryAgain')}
          </button>
        </div>
      </div>
    );
  }

  if (!data || !data.products || data.products.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>{t('dashboard.topProducts')}</h3>
          <div className={selectStyles.headerActions}>
            <DateRangePresetSelect value={datePreset} onChange={setDatePreset} />
            <Award size={20} />
          </div>
        </div>
        <div className={styles.emptyState}>
          <Package size={48} className={styles.emptyIcon} />
          <p>{t('dashboard.noProductsFound')}</p>
        </div>
      </div>
    );
  }

  const maxQuantity = getMaxQuantity();

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>{t('dashboard.topProducts')}</h3>
        <div className={selectStyles.headerActions}>
          <DateRangePresetSelect value={datePreset} onChange={setDatePreset} />
          <Award size={20} />
        </div>
      </div>

      <div className={styles.content}>
        {data.products.map((product, index) => {
          const rank = index + 1;
          const percentage = maxQuantity > 0 ? (product.quantitySold / maxQuantity) * 100 : 0;
          
          return (
            <div key={index} className={styles.productItem}>
              <div className={styles.productHeader}>
                <div className={styles.productInfo}>
                  <div 
                    className={styles.rankBadge}
                    style={{ backgroundColor: getRankBadgeColor(rank) }}
                  >
                    {rank}
                  </div>
                  <div className={styles.productDetails}>
                    <span className={styles.productName} title={product.productName}>
                      {product.productName}
                    </span>
                    <span className={styles.productMeta}>
                      {formatNumber(product.orderCount)} {t('dashboard.orders')}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className={styles.progressContainer}>
                <div className={styles.progressBar}>
                  <div 
                    className={styles.progressFill}
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <div className={styles.quantityBadge}>
                  {formatNumber(product.quantitySold)}
                </div>
              </div>
              
              <div className={styles.productStats}>
                <div className={styles.stat}>
                  <span className={styles.statLabel}>{t('dashboard.quantitySold')}</span>
                  <span className={styles.statValue}>{formatNumber(product.quantitySold)}</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statLabel}>{t('dashboard.revenueGenerated')}</span>
                  <span className={styles.statValue}>{formatCurrency(product.revenue, currency)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TopSellingProducts;